import mustache from "mustache";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import {
  agruparPorSemana,
  formatDiaMes,
  formatRangoSemana,
  SemanaMonitoreo,
} from "../../../../../modulos/monitoreo/sesion/agruparPorSemana";
import { takeSeleccionReporte } from "../../../../../modulos/monitoreo/sesion/seleccionReportePendiente";
import { SesionDashboardItem } from "../../../../../modulos/monitoreo/sesion/response/SesionDashboardResponse";
import { fx_createSesionDashboardGrpcService } from "../../../../../modulos/monitoreo/sesion/services/SesionDashboardGrpcService";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import { cargarInformeSemanalAsync } from "./cargarInformeSemanal";
import { openReporteMonitoreo } from "./openReporteMonitoreo";
import { buildReporteMonitoreoHtml } from "./reporteMonitoreoDocument";
import template from "./monitoreoReporteTemplate.html";

mustache.parse(template);

/** Contador incremental de instancias, para no colisionar ids en el DOM. */
let CONTENT_ID = 0;

/** Cuerpo de la ruta `/monitoreo/plagas`: el informe de una **semana**.
 *
 *  La unidad es la semana, no el recorrido: el monitoreo se reparte en varias
 *  salidas —unos lotes el lunes, otros el miércoles—, así que el informe de una
 *  sesión suelta mostraba los lotes de esa salida nada más ("Lotes
 *  monitoreados: 1" en una finca con cinco). Además el desplegable repetía
 *  "Semana 35" una vez por recorrido, y los que caían el mismo día quedaban
 *  indistinguibles entre sí.
 *
 *  Muestra el **mismo documento** que la exportación imprimible, embebido en un
 *  `<iframe>` en modo empotrado (sin su barra superior). Se hace así, y no
 *  portando el reporte a componentes de la app, para que exista una sola
 *  implementación: `buildReporteMonitoreoHtml` sigue siendo el único lugar
 *  donde vive el reporte, y lo que se ve en pantalla es exactamente lo que sale
 *  impreso.
 *
 *  El botón "Exportar a PDF" delega en `openReporteMonitoreo`, que abre la
 *  versión suelta en pestaña nueva y dispara ahí el diálogo de impresión (donde
 *  el usuario elige "Guardar como PDF" o una impresora). No se imprime el
 *  iframe directamente porque hacerlo depende de qué frame tenga el foco y no
 *  es confiable entre navegadores. */
export class MonitoreoReporteContentRender implements IConnectRender {
  /** @param _onBack Acción del botón volver (la ruta la decide la página). */
  constructor(private readonly _onBack: () => void) {
    CONTENT_ID += 1;
    this._id = CONTENT_ID;
  }

  render(): string {
    return mustache.render(template, {
      id: this._id,
      nombreFinca: serviceProvider.getFincaSelectedService().getRequired()
        .nombreFinca,
    });
  }

  connect(): void {
    this._backBtn = document.getElementById(`repBack-${this._id}`);
    this._controlsEl = document.getElementById(`repControls-${this._id}`);
    this._anioSelect = document.getElementById(
      `repAnio-${this._id}`,
    ) as HTMLSelectElement | null;
    this._semanaSelect = document.getElementById(
      `repSesion-${this._id}`,
    ) as HTMLSelectElement | null;
    this._printBtn = document.getElementById(`repPrint-${this._id}`);
    this._bodyEl = document.getElementById(`repBody-${this._id}`);
    this._frameEl = document.getElementById(
      `repFrame-${this._id}`,
    ) as HTMLIFrameElement | null;
    this._stateEl = document.getElementById(`repState-${this._id}`);
    this._fechasEl = document.getElementById(`repFechas-${this._id}`);

    this._backBtn?.addEventListener("click", this._onBack);
    this._anioSelect?.addEventListener("change", this._onAnioChange);
    this._semanaSelect?.addEventListener("change", this._onSemanaChange);
    this._printBtn?.addEventListener("click", this._onPrintClick);
    this._fechasEl?.addEventListener("click", this._onFechaClick);

    void this._loadAsync();
  }

  dispose(): void {
    this._disposed = true;
    this._backBtn?.removeEventListener("click", this._onBack);
    this._anioSelect?.removeEventListener("change", this._onAnioChange);
    this._semanaSelect?.removeEventListener("change", this._onSemanaChange);
    this._printBtn?.removeEventListener("click", this._onPrintClick);
    this._fechasEl?.removeEventListener("click", this._onFechaClick);
    this._revokeFrameUrl();
  }

  /** Id de instancia, para los ids del DOM. */
  private readonly _id: number;

  private _backBtn: HTMLElement | null = null;
  private _controlsEl: HTMLElement | null = null;
  private _anioSelect: HTMLSelectElement | null = null;
  private _semanaSelect: HTMLSelectElement | null = null;
  private _printBtn: HTMLElement | null = null;
  private _bodyEl: HTMLElement | null = null;
  private _frameEl: HTMLIFrameElement | null = null;
  private _stateEl: HTMLElement | null = null;
  private _fechasEl: HTMLElement | null = null;

  /** Índice de la semana mostrada dentro de `_semanas`. */
  private _indice = 0;
  /** Recorrido mostrado, o `null` para la semana completa. */
  private _sesionId: string | null = null;

  /** Si el componente ya fue dispuesto (guarda para las cargas async). */
  private _disposed = false;

  /** Todas las sesiones de la finca, ascendente por fecha. */
  private _todasSesiones: SesionDashboardItem[] = [];

  /** Semanas del año elegido, de la más reciente a la más vieja. Es lo que
   *  lista el desplegable, y el índice de cada opción apunta acá. */
  private _semanas: SemanaMonitoreo[] = [];

  /** URL del blob que está mostrando el iframe. Se revoca al reemplazarla: si
   *  no, cada semana vista deja un documento retenido en memoria hasta que se
   *  cierre la pestaña. */
  private _frameUrl: string | null = null;

  /** Id de la última petición disparada. Las respuestas fuera de orden se
   *  descartan comparando contra este valor: sin esto, cambiar de semana dos
   *  veces rápido puede dejar en pantalla el informe de la primera si esa
   *  respuesta llega segunda. */
  private _peticionActual = 0;

  /** Carga el histórico y muestra el informe de la semana más reciente. */
  private async _loadAsync(): Promise<void> {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    try {
      const dashboard =
        await fx_createSesionDashboardGrpcService(finca.id).getAsync();
      if (this._disposed) return;

      this._todasSesiones = dashboard.sesiones;
      if (this._todasSesiones.length === 0) {
        this._setState("Esta finca todavía no tiene sesiones de monitoreo.");
        return;
      }

      this._renderAnioSelect();
      if (this._controlsEl) this._controlsEl.style.display = "";

      // Si se llegó desde una fecha del mapa, se abre en ese informe; si no,
      // en la semana más reciente. `take` lo consume: entrar después por otro
      // camino no debe revivir una selección vieja.
      const pedida = takeSeleccionReporte();
      const anio = pedida
        ? Number(pedida.fecha.slice(0, 4))
        : this._getAniosDisponibles()[0];

      if (this._anioSelect) this._anioSelect.value = String(anio);
      this._aplicarAnio(anio, pedida);
    } catch {
      if (this._disposed) return;
      this._setState("No se pudieron cargar las sesiones de monitoreo.");
    }
  }

  /** Años con sesiones, del más reciente al más viejo. */
  private _getAniosDisponibles(): number[] {
    const anios = new Set(
      this._todasSesiones.map((sesion) => Number(sesion.fecha.slice(0, 4))),
    );
    return [...anios].sort((a, b) => b - a);
  }

  /** Puebla el selector de año; se oculta si solo hay uno. */
  private _renderAnioSelect(): void {
    if (!this._anioSelect) return;
    const anios = this._getAniosDisponibles();
    this._anioSelect.innerHTML = anios
      .map((anio) => `<option value="${anio}">${anio}</option>`)
      .join("");
    this._anioSelect.style.display = anios.length > 1 ? "" : "none";
  }

  /** Puebla el selector con las **semanas** del año elegido y muestra una: la
   *  pedida al llegar desde el mapa, o la más reciente.
   *
   *  @param pedida Semana y recorrido que se venían mirando, si se llegó desde
   *                el mapa. Si esa semana no está en el año (o el recorrido no
   *                está en la semana), se cae a lo más reciente en vez de
   *                fallar: la selección viene de otra pantalla y no hay
   *                garantía de que siga existiendo. */
  private _aplicarAnio(
    anio: number,
    pedida?: { semana: number; sesionId: string | null } | null,
  ): void {
    if (!this._semanaSelect) return;

    const sesionesDelAnio = this._todasSesiones.filter(
      (sesion) => Number(sesion.fecha.slice(0, 4)) === anio,
    );
    this._semanas = agruparPorSemana(sesionesDelAnio).reverse();

    this._semanaSelect.innerHTML = this._semanas
      .map((semana, indice) => {
        // Se dice cuántas salidas cubre: explica por qué el informe puede
        // traer más lotes que un solo recorrido.
        const salidas =
          semana.sesiones.length > 1
            ? ` · ${semana.sesiones.length} recorridos`
            : "";
        const abierta = semana.tieneSinFinalizar ? " · sin finalizar" : "";
        return `<option value="${indice}">Semana ${semana.semana} · ${formatRangoSemana(semana)}${salidas}${abierta}</option>`;
      })
      .join("");

    if (this._semanas.length === 0) return;

    const indice = pedida
      ? this._semanas.findIndex((semana) => semana.semana === pedida.semana)
      : -1;
    this._indice = indice >= 0 ? indice : 0;
    this._semanaSelect.value = String(this._indice);

    // El recorrido solo vale si pertenece a la semana que se terminó
    // mostrando: si se cayó a otra, la selección de día ya no significa nada.
    const semana = this._semanas[this._indice];
    this._sesionId =
      indice >= 0 &&
      pedida?.sesionId &&
      semana.sesiones.some((sesion) => sesion.id === pedida.sesionId)
        ? pedida.sesionId
        : null;

    void this._mostrarAsync();
  }

  /** Pide los detalles de lo seleccionado (`_indice` + `_sesionId`), los
   *  fusiona y carga el informe en el iframe. */
  private async _mostrarAsync(): Promise<void> {
    const semana = this._semanas[this._indice];
    if (!semana) return;

    this._renderFechas(semana);

    this._peticionActual += 1;
    const peticion = this._peticionActual;

    this._setState("Generando el informe…");
    if (this._bodyEl) this._bodyEl.style.display = "none";

    try {
      const { informe, plagas, poligonos } = await cargarInformeSemanalAsync(
        this._getSesionIds(semana),
      );
      // Descartar si el componente murió o si ya se pidió otra semana.
      if (this._disposed || peticion !== this._peticionActual) return;

      const html = buildReporteMonitoreoHtml(informe, plagas, {
        embebido: true,
        poligonos,
      });
      this._revokeFrameUrl();
      this._frameUrl = URL.createObjectURL(
        new Blob([html], { type: "text/html;charset=utf-8" }),
      );
      if (this._frameEl) this._frameEl.src = this._frameUrl;

      this._setState("");
      if (this._bodyEl) this._bodyEl.style.display = "";
    } catch {
      if (this._disposed || peticion !== this._peticionActual) return;
      this._setState("No se pudo generar el informe.");
    }
  }

  /**
   * Recorrido que se está mirando.
   *
   * Con una sola salida en la semana, `_sesionId` puede estar en `null` pero lo
   * que se ve **es** ese recorrido: se resuelve acá para que el chip marcado y
   * el informe salgan del mismo valor. Mismo criterio que en el sidebar del
   * mapa, para que las dos pantallas nombren lo mismo igual.
   */
  private _getSesionActiva(semana: SemanaMonitoreo): string | null {
    if (this._sesionId) return this._sesionId;
    return semana.sesiones.length === 1 ? semana.sesiones[0].id : null;
  }

  /** Sesiones que entran al informe: una sola si hay un día elegido, todas las
   *  de la semana si no. */
  private _getSesionIds(semana: SemanaMonitoreo): string[] {
    return this._sesionId
      ? [this._sesionId]
      : semana.sesiones.map((sesion) => sesion.id);
  }

  /** Chips de recorrido de la semana mostrada.
   *
   *  Existen para que llegar a un día no sea un callejón: sin ellos el
   *  selector diría "Semana 35" mostrando una sola salida, y no habría forma
   *  de volver a la semana completa. */
  private _renderFechas(semana: SemanaMonitoreo): void {
    if (!this._fechasEl) return;

    const activa = this._getSesionActiva(semana);
    const chip = (id: string, etiqueta: string, esActiva: boolean): string =>
      `<button class="mon-reporte-fecha${esActiva ? " mon-reporte-fecha--active" : ""}" data-sesion="${id}">${etiqueta}</button>`;

    const partes: string[] = [];
    // "Toda la semana" solo con más de una salida: con una sola sería la misma
    // cosa dicha dos veces.
    if (semana.sesiones.length > 1) {
      partes.push(chip("", "Toda la semana", activa === null));
    }
    for (const sesion of semana.sesiones) {
      partes.push(chip(sesion.id, formatDiaMes(sesion.fecha), sesion.id === activa));
    }

    this._fechasEl.innerHTML = partes.join("");
    this._fechasEl.style.display = "";
  }

  /** Libera el blob que está mostrando el iframe. */
  private _revokeFrameUrl(): void {
    if (!this._frameUrl) return;
    URL.revokeObjectURL(this._frameUrl);
    this._frameUrl = null;
  }

  /** Mensaje de estado (cargando / vacío / error); vacío lo oculta. */
  private _setState(texto: string): void {
    if (!this._stateEl) return;
    this._stateEl.textContent = texto;
    this._stateEl.style.display = texto ? "" : "none";
  }

  private _onAnioChange = (): void => {
    if (!this._anioSelect) return;
    this._aplicarAnio(Number(this._anioSelect.value));
  };

  /** Cambiar de semana descarta el día elegido: un recorrido pertenece a una
   *  semana, y arrastrarlo a otra no significaría nada. */
  private _onSemanaChange = (): void => {
    if (!this._semanaSelect) return;
    this._indice = Number(this._semanaSelect.value);
    this._sesionId = null;
    void this._mostrarAsync();
  };

  private _onFechaClick = (event: Event): void => {
    const chip = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-sesion]",
    );
    if (!chip) return;

    // `data-sesion=""` es "toda la semana".
    const sesionId = chip.dataset.sesion || null;
    if (sesionId === this._sesionId) return;
    this._sesionId = sesionId;
    void this._mostrarAsync();
  };

  /** Imprime exactamente lo que está en pantalla: el día elegido o la semana. */
  private _onPrintClick = (): void => {
    const semana = this._semanas[this._indice];
    if (!semana) return;
    void openReporteMonitoreo(this._getSesionIds(semana));
  };
}
